// src/stores/gamificationStore.js - Zustand Gamification Store for Profound Philosophy
import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { achievements, pointValues, getLevelInfo } from '../data/gamification';
import { philosophyChallenges } from '../data/philosophyChallenges';

// Per-module badges (data file ids match src/data/modules.js slugs exactly)
const MODULE_BADGES = {
  'what-is-philosophy': 'EXAMINED_LIFE',
  'logic-and-reasoning': 'CLEAR_THINKER',
  'epistemology': 'KNOWLEDGE_SEEKER',
  'ethics-moral-philosophy': 'MORAL_COMPASS',
  'metaphysics-reality': 'REALITY_EXPLORER',
  'philosophy-of-mind': 'CONSCIOUSNESS_INVESTIGATOR',
  'social-political-philosophy': 'JUSTICE_ADVOCATE',
  'existentialism-meaning': 'MEANING_MAKER'
};
import { authHubAuth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { hydrateAppState, queuePatch } from '../services/hubAppState';
import { triggerQuickCelebration } from '../components/shared/CelebrationAnimation';

const GAMIFICATION_VERSION = '1.0';

// Helper to get current user ID — the live hub session first, then the
// AuthContext localStorage mirror (which may lag the session on cold start).
const getCurrentUserId = () => {
  try {
    if (authHubAuth?.currentUser?.uid) return authHubAuth.currentUser.uid;
    const authData = localStorage.getItem('profoundPhilosophy_userId');
    if (authData) {
      return authData;
    }
    return null; // Guest user
  } catch (error) {
    console.warn('Error getting user ID:', error);
    return null;
  }
};

// Cloud sync — the gamification section of the hub thin-layer document
// (users/{uid}/apps/profound-philosophy). Identity comes from the hub
// session server-side; userId only gates whether to sync at all.
const syncToFirebase = async (userId, data) => {
  if (!userId) return; // Don't sync for guest users

  try {
    queuePatch({
      gamification: {
        ...data,
        lastUpdated: new Date().toISOString(),
        version: GAMIFICATION_VERSION,
      },
    });
  } catch (error) {
    console.error('Gamification hub sync failed:', error);
  }
};

const loadFromFirebase = async (userId) => {
  if (!userId) return null;

  try {
    const hubDoc = await hydrateAppState();
    return hubDoc?.gamification || null;
  } catch (error) {
    console.error('Gamification hub load failed:', error);
    return null;
  }
};

// Create the Zustand store
export const useGamificationStore = create(
  devtools(
    persist(
      (set, get) => ({
        // State
        totalPoints: 0,
        currentLevel: 1,
        completedModules: [],
        completedTools: [],
        unlockedAchievements: [],
        pendingAchievements: [],
        currentStreak: 0,
        longestStreak: 0,
        lastActivity: null,
        isInitialized: false,
        completedWeeklyChallenges: [],

        // Computed values (getters)
        getCurrentLevelData: () => {
          const points = get().totalPoints;
          return getLevelInfo(points);
        },

        getPointsToNextLevel: () => {
          const levelInfo = get().getCurrentLevelData();
          return levelInfo.pointsToNextLevel;
        },

        // Actions
        addPoints: (amount, _reason = '') => {
          const state = get();
          const newPoints = state.totalPoints + amount;
          const oldLevelInfo = getLevelInfo(state.totalPoints);
          const newLevelInfo = getLevelInfo(newPoints);
          const leveledUp = newLevelInfo.currentLevel.level > oldLevelInfo.currentLevel.level;

          set({
            totalPoints: newPoints,
            currentLevel: newLevelInfo.currentLevel.level,
            lastActivity: new Date().toISOString(),
          });

          // Trigger celebration
          if (leveledUp) {
            const newLevel = newLevelInfo.currentLevel;
            triggerQuickCelebration(`Level ${newLevel.level}: ${newLevel.title}`);
          }

          // Sync to Firebase (async, non-blocking)
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              totalPoints: newPoints,
              currentLevel: newLevelInfo.currentLevel.level,
              lastActivity: new Date().toISOString(),
            });
          }
        },

        completeModule: (moduleId) => {
          const state = get();

          // Don't complete twice
          if (state.completedModules.includes(moduleId)) {
            return;
          }

          const newCompleted = [...state.completedModules, moduleId];

          set({
            completedModules: newCompleted,
            lastActivity: new Date().toISOString(),
          });

          // Award points
          get().addPoints(pointValues.COMPLETE_MODULE, `Completed Module ${moduleId}`);

          // Per-module badge (these existed as dead data until 2026-08-16)
          if (MODULE_BADGES[moduleId]) {
            get().unlockAchievement(MODULE_BADGES[moduleId]);
          }

          // Check for achievements
          get().checkModuleAchievements(newCompleted);

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              completedModules: newCompleted,
              lastActivity: new Date().toISOString(),
            });
          }
        },

        unlockAchievement: (achievementId) => {
          const state = get();

          if (state.unlockedAchievements.includes(achievementId)) {
            return;
          }

          const achievement = achievements[achievementId];
          if (!achievement) return;

          set({
            unlockedAchievements: [...state.unlockedAchievements, achievementId],
            pendingAchievements: [...state.pendingAchievements, achievementId],
          });

          // Award achievement points
          if (achievement.points) {
            get().addPoints(achievement.points, `Unlocked: ${achievement.name}`);
          }

          // Celebration
          triggerQuickCelebration(`${achievement.icon} ${achievement.name}`);

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              unlockedAchievements: get().unlockedAchievements,
              pendingAchievements: get().pendingAchievements,
            });
          }
        },

        updateStreak: () => {
          const state = get();
          const now = new Date();
          const lastActivity = state.lastActivity ? new Date(state.lastActivity) : null;

          if (!lastActivity) {
            // First activity
            set({
              currentStreak: 1,
              longestStreak: 1,
              lastActivity: now.toISOString(),
            });
            get().addPoints(pointValues.DAILY_LOGIN, 'Daily login');
            return;
          }

          const daysSinceLastActivity = Math.floor(
            (now - lastActivity) / (1000 * 60 * 60 * 24)
          );

          let newStreak = state.currentStreak;

          if (daysSinceLastActivity === 0) {
            // Same day - no change
            return;
          } else if (daysSinceLastActivity === 1) {
            // Consecutive day - increment
            newStreak = state.currentStreak + 1;
            get().addPoints(pointValues.DAILY_LOGIN, 'Daily login streak');

            // Check streak achievements (each badge carries its own points —
            // paying pointValues.STREAK_* here as well double-paid the event)
            if (newStreak === 7) {
              get().unlockAchievement('WEEK_WARRIOR');
            } else if (newStreak === 30) {
              get().unlockAchievement('MONTH_MASTER');
            } else if (newStreak === 100) {
              get().unlockAchievement('CENTURION');
            }
          } else {
            // Streak broken - reset
            newStreak = 1;
            get().addPoints(pointValues.DAILY_LOGIN, 'Daily login');
          }

          const newLongestStreak = Math.max(newStreak, state.longestStreak);

          set({
            currentStreak: newStreak,
            longestStreak: newLongestStreak,
            lastActivity: now.toISOString(),
          });

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              currentStreak: newStreak,
              longestStreak: newLongestStreak,
              lastActivity: now.toISOString(),
            });
          }
        },

        clearPendingAchievements: () => {
          set({ pendingAchievements: [] });
        },

        checkModuleAchievements: (completedModules) => {
          // Check for milestone achievements
          if (completedModules.length === 1) {
            get().unlockAchievement('FIRST_STEPS');
          }
          if (completedModules.length >= 8) {
            get().unlockAchievement('MODULE_MASTER');
            get().addPoints(pointValues.FINISH_ALL_MODULES, 'Completed all modules');
          }
        },

        checkToolAchievements: (completedTools) => {
          // Check for tool milestone achievements
          if (completedTools.length === 1) {
            get().unlockAchievement('TOOL_EXPLORER');
          }
          // The Toolkit registry has 18 tools; the badge says all of them.
          if (completedTools.length >= 18) {
            get().unlockAchievement('TOOL_MASTER');
            get().addPoints(pointValues.USE_ALL_TOOLS, 'Used every tool in the Toolkit');
          }
        },

        completeTool: (toolId) => {
          const state = get();

          // Don't complete twice
          if (state.completedTools.includes(toolId)) {
            return;
          }

          const newCompleted = [...state.completedTools, toolId];

          set({
            completedTools: newCompleted,
            lastActivity: new Date().toISOString(),
          });

          // Award points
          get().addPoints(pointValues.FIRST_TOOL_USE, `Completed Tool ${toolId}`);

          // Check for achievements
          get().checkToolAchievements(newCompleted);

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              completedTools: newCompleted,
              lastActivity: new Date().toISOString(),
            });
          }
        },

        useToolSession: (toolId) => {
          get().addPoints(pointValues.TOOL_SESSION, `Used ${toolId}`);
        },

        completeWeeklyChallenge: (challengeId, difficulty) => {
          const state = get();

          // Don't complete twice
          if (state.completedWeeklyChallenges.includes(challengeId)) {
            return;
          }

          const newCompleted = [...state.completedWeeklyChallenges, challengeId];

          set({
            completedWeeklyChallenges: newCompleted,
            lastActivity: new Date().toISOString(),
          });

          // Award the points the challenge itself advertises (single source of
          // truth = the data file; the old difficulty-tier table disagreed with
          // the number shown on the Dashboard card)
          const challenge = philosophyChallenges.find(c => c.id === challengeId);
          const points = challenge?.points ??
                         (difficulty === 'Beginner' ? pointValues.WEEKLY_CHALLENGE_BEGINNER :
                          difficulty === 'Intermediate' ? pointValues.WEEKLY_CHALLENGE_INTERMEDIATE :
                          pointValues.WEEKLY_CHALLENGE_ADVANCED);

          get().addPoints(points, `Completed ${difficulty} challenge`);

          // Check challenge achievements
          if (newCompleted.length === 1) {
            get().unlockAchievement('CHALLENGE_ACCEPTED');
          } else if (newCompleted.length === 10) {
            get().unlockAchievement('CHALLENGE_CRUSHER');
            get().addPoints(pointValues.WEEKLY_CHALLENGES_10, '10 challenges completed');
          } else if (newCompleted.length === 25) {
            get().unlockAchievement('CHALLENGE_LEGEND');
          }

          // Sync to Firebase
          const userId = getCurrentUserId();
          if (userId) {
            syncToFirebase(userId, {
              completedWeeklyChallenges: newCompleted,
              lastActivity: new Date().toISOString(),
            });
          }
        },

        completeAssessment: () => {
          get().addPoints(pointValues.COMPLETE_ASSESSMENT, 'Completed assessment');
          get().unlockAchievement('ASSESSMENT_COMPLETE');
        },

        retakeAssessment: () => {
          get().addPoints(pointValues.RETAKE_ASSESSMENT, 'Retook assessment');
          get().unlockAchievement('GROWTH_MINDSET');
        },

        // Initialize from Firebase
        initializeFromFirebase: async () => {
          const userId = getCurrentUserId();
          if (!userId) {
            set({ isInitialized: true });
            // Still update streak for guest users
            get().updateStreak();
            return;
          }

          try {
            const firebaseData = await loadFromFirebase(userId);

            if (firebaseData) {
              set({
                totalPoints: firebaseData.totalPoints || 0,
                currentLevel: firebaseData.currentLevel || 1,
                completedModules: firebaseData.completedModules || [],
                completedTools: firebaseData.completedTools || [],
                unlockedAchievements: firebaseData.unlockedAchievements || [],
                currentStreak: firebaseData.currentStreak || 0,
                longestStreak: firebaseData.longestStreak || 0,
                lastActivity: firebaseData.lastActivity || null,
                completedWeeklyChallenges: firebaseData.completedWeeklyChallenges || [],
                isInitialized: true,
              });
            } else {
              set({ isInitialized: true });
            }

            // Update streak on login
            get().updateStreak();
          } catch (error) {
            console.error('Failed to initialize gamification from Firebase:', error);
            set({ isInitialized: true });
          }
        },

        // Clear all data (for logout)
        clearUserData: () => {
          set({
            totalPoints: 0,
            currentLevel: 1,
            completedModules: [],
            completedTools: [],
            unlockedAchievements: [],
            pendingAchievements: [],
            currentStreak: 0,
            longestStreak: 0,
            lastActivity: null,
            completedWeeklyChallenges: [],
          });
        },
      }),
      {
        name: 'profound-philosophy-gamification',
        partialize: (state) => ({
          totalPoints: state.totalPoints,
          currentLevel: state.currentLevel,
          completedModules: state.completedModules,
          completedTools: state.completedTools,
          unlockedAchievements: state.unlockedAchievements,
          currentStreak: state.currentStreak,
          longestStreak: state.longestStreak,
          lastActivity: state.lastActivity,
          completedWeeklyChallenges: state.completedWeeklyChallenges,
        }),
      }
    ),
    { name: 'GamificationStore' }
  )
);

// Hook to initialize on app start. The hub hydrate needs the restored hub
// session, which is not yet available at module-load time on a cold start —
// wait for the first auth state emission (signed in or not) before reading.
export const initializeGamification = () => {
  if (authHubAuth) {
    const unsubscribe = onAuthStateChanged(authHubAuth, () => {
      unsubscribe();
      useGamificationStore.getState().initializeFromFirebase();
    });
  } else {
    useGamificationStore.getState().initializeFromFirebase();
  }
};

// Export for use
export const useGamification = useGamificationStore;
